import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface GovernancePack {
  id: string;
  industry: string;
  version: number | null;
  public_trust_charter: string | null;
  internal_rules: unknown;
  created_at: string | null;
}

const AdminGovernance: React.FC = () => {
  const [packs, setPacks] = useState<GovernancePack[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        const { data, error } = await supabase
          .from('governance_packs')
          .select('*')
          .order('industry', { ascending: true });

        if (error) throw error;
        setPacks(data || []);
      } catch (error) {
        console.error('Error fetching governance packs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPacks();
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Governance Packs</h1>
            <p className="text-muted-foreground mt-1">
              Industry-specific rules and trust charters for bot behavior
            </p>
          </div>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Create Pack
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : packs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No governance packs found</h3>
              <p className="text-muted-foreground mt-1">
                Create your first governance pack to define bot behavior rules
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {packs.map((pack) => (
              <Card key={pack.id}>
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => setExpandedId(expandedId === pack.id ? null : pack.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg capitalize">
                          {pack.industry} Governance
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          Version {pack.version || 1}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline">
                      {pack.industry}
                    </Badge>
                  </div>
                </CardHeader>
                
                {expandedId === pack.id && (
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Public Trust Charter</h4>
                      <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                        {pack.public_trust_charter || 'No trust charter defined'}
                      </p>
                    </div>
                    
                    {pack.internal_rules && (
                      <div>
                        <h4 className="font-medium mb-2">Internal Rules</h4>
                        <pre className="text-sm bg-muted p-3 rounded-lg overflow-auto max-h-64">
                          {JSON.stringify(pack.internal_rules, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">Edit Pack</Button>
                      <Button variant="outline" size="sm">Clone</Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGovernance;
